export const focusAreas: Record<string, any> = {
  insightExtraction: {
    title: "AI-Powered Insight Extraction",
    description:
      "Unlock hidden patterns and opportunities in your data with advanced AI algorithms that automatically surface the insights that matter most to your business.",
    symptoms: [
      "Valuable data sitting unused because analysis is too time-consuming",
      "Missing important trends and patterns in your business metrics",
      "Spending hours manually searching through data for answers",
      "Insights arrive too late to act on opportunities",
    ],
    impacts: [
      "Competitive disadvantage from slow decision-making",
      "Missed revenue opportunities hidden in your data",
      "Reactive business decisions instead of proactive strategy",
      "Team frustration with inability to access needed insights",
    ],
    solutions: [
      "Deploy AI algorithms that automatically analyze data and surface key insights",
      "Implement machine learning models that identify patterns humans miss",
      "Create automated anomaly detection to catch issues before they escalate",
      "Build intelligent reporting that highlights what matters most",
    ],
    nextSteps: [
      {
        step: "Identify Key Questions",
        action:
          "List the critical business questions you need answered regularly from your data.",
      },
      {
        step: "Consolidate Data Sources",
        action:
          "Bring together scattered data into a unified analytics platform for comprehensive insights.",
      },
      {
        step: "Deploy AI Analytics",
        action:
          "Implement AI-powered tools that automatically analyze data and generate insights.",
      },
      {
        step: "Automate Insight Delivery",
        action:
          "Set up automated reports and alerts that deliver insights when and where you need them.",
      },
    ],
    insight:
      "Organizations using AI-powered analytics discover 5x more actionable insights and make decisions 10x faster than those relying on manual data analysis.",
  },
  dataVisualization: {
    title: "Intelligent Data Visualization",
    description:
      "Transform complex data into clear, actionable visual insights that everyone can understand, with AI-generated dashboards that automatically adapt to show what's most important.",
    symptoms: [
      "Data visualizations are static and quickly become outdated",
      "Team members struggle to understand complex reports and charts",
      "Creating custom visualizations takes significant time and expertise",
      "Important metrics are buried in generic dashboards",
    ],
    impacts: [
      "Poor decision-making due to unclear data presentation",
      "Time wasted creating and updating manual visualizations",
      "Communication gaps when data isn't presented effectively",
      "Missed insights because patterns aren't visually apparent",
    ],
    solutions: [
      "Implement AI that automatically generates optimal visualizations for your data",
      "Deploy dynamic dashboards that update in real-time with latest information",
      "Create role-based views that show each team member what they need",
      "Build interactive visualizations that let users explore data naturally",
    ],
    nextSteps: [
      {
        step: "Assess Current Reporting",
        action:
          "Evaluate which visualizations are most valuable and which need improvement.",
      },
      {
        step: "Define User Needs",
        action:
          "Understand what different roles need to see and how they prefer to consume data.",
      },
      {
        step: "Implement Smart Dashboards",
        action:
          "Deploy AI-powered visualization tools that auto-generate the best charts for your data.",
      },
      {
        step: "Enable Self-Service",
        action:
          "Give teams the ability to create their own visualizations without technical skills.",
      },
    ],
    insight:
      "Companies using AI-driven visualization tools reduce reporting time by 75% and increase data comprehension by 90% across their organization.",
  },
  decisionSupport: {
    title: "AI-Driven Decision Support",
    description:
      "Make better, faster decisions with AI systems that analyze your data, predict outcomes, and provide clear recommendations based on advanced algorithms and business context.",
    symptoms: [
      "Decisions delayed while waiting for data analysis",
      "Uncertainty about which option will yield best results",
      "Relying on intuition when data should drive choices",
      "Difficulty understanding the impact of different decisions",
    ],
    impacts: [
      "Slower response to market changes and opportunities",
      "Inconsistent decision quality across the organization",
      "Missed optimization opportunities in operations and strategy",
      "Higher risk from decisions made without data backing",
    ],
    solutions: [
      "Deploy AI recommendation engines that suggest optimal actions based on data",
      "Implement predictive models that forecast outcomes of different decisions",
      "Create what-if scenario analysis tools powered by machine learning",
      "Build decision frameworks that combine AI insights with business rules",
    ],
    nextSteps: [
      {
        step: "Map Decision Points",
        action:
          "Identify the key decisions where AI support would have the highest impact.",
      },
      {
        step: "Define Success Metrics",
        action:
          "Establish clear criteria for what makes a good decision in each area.",
      },
      {
        step: "Deploy AI Recommendations",
        action:
          "Implement AI systems that analyze options and recommend best actions.",
      },
      {
        step: "Create Feedback Loops",
        action:
          "Track decision outcomes to continuously improve AI recommendation accuracy.",
      },
    ],
    insight:
      "Businesses leveraging AI decision support improve decision quality by 65%, reduce decision time by 80%, and increase confidence in data-driven choices by 95%.",
  },
};
